import { useContext, useCallback, useRef, useState } from 'react';
import { IEditorProps } from './Editor';
import { FileType } from '@decisions/server/DecisionsFramework/Design/Properties/Attributes';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { submitFileReference } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { EditorLabel } from './EditorLabel';
import { FileUploadArea } from '../FileLoader/Upload/FileUploadArea';
import { IUploadedFileInfo } from '../FileLoader/Upload/FileInfo';
import { getChildValue } from 'components/property-editors/utils/ProviderNodeUtils';
import { generateGuid } from 'utils/Guid';
import { EditorValidationWrapper } from './EditorValidationWrapper';

interface IFileContentPickerEditorProps extends IEditorProps {}

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function FileContentPickerEditor(props: IFileContentPickerEditorProps) {
  const { child, path, onChange } = props;
  const { editingContextId } = useContext(PropertyEditorContext);
  const [fileAreaWarningShowing, setFileAreaWarningShowing] = useState(false);
  const fileType: FileType = getEditorConfigValue(child, 'FileType');
  const bytes: number[] | null = getChildValue(child);
  const length = bytes?.length || 0;
  const id = useRef(generateGuid().toString());
  let uploadedFile: IUploadedFileInfo[] = [];

  const allowedExtensions =
    fileType === FileType.ImageType ? ['jpg', 'png', 'gif', 'bmp', 'tiff', 'jpeg', 'tiff', 'ico'] : null;

  const onComplete = useCallback(
    async (fileInfo: IUploadedFileInfo) => {
      await submitFileReference({ editingContextId, fileReferenceId: fileInfo.fileId, path });
    },
    [editingContextId, path]
  );

  var resetUploadedFile = useCallback(() => {
    onChange(null, path);
  }, [onChange, path]);

  function getUploadedFileInfo(): IUploadedFileInfo[] {
    if (bytes && bytes.length > 0) {
      let file: IUploadedFileInfo = {
        fileId: id.current,
        fileName: 'Content',
        fileSize: length,
      };
      uploadedFile.push(file);
    }
    return uploadedFile;
  }

  return (
    <EditorLabel htmlFor={props.id} editor={child} path={path} doNotWrap>
      <EditorValidationWrapper editor={child} shouldShow={!fileAreaWarningShowing}>
        <div className="dp-file-editor-holder">
          <FileUploadArea
            onRemove={resetUploadedFile}
            onComplete={onComplete}
            allowedExtensions={allowedExtensions ?? undefined}
            uploadedFileInfo={getUploadedFileInfo() ?? undefined}
            disabledDownload={true}
            holderClassName="dp-file-editor-area-holder"
            onFileAreaWarningsShowing={setFileAreaWarningShowing}
          />
        </div>
      </EditorValidationWrapper>
    </EditorLabel>
  );
}
